import { Suspense } from 'react'
import {
  QueryClient,
  QueryClientProvider,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { FontSizeSelector } from './FontSizeSelector'
import './overrides.css'
import type { fetchCollects, fetchLiturgy } from '../api'

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000 } },
})

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense>
        <Page />
      </Suspense>
    </QueryClientProvider>
  )
}

function Page() {
  const { data: liturgyData } = useSuspenseQuery({
    queryKey: ['liturgy'],
    queryFn: async function () {
      const response = await fetch('/api/liturgy')
      const json = (await response.json()) as ReturnType<typeof fetchLiturgy>
      return json
    },
  })

  const {
    calendar_day: {
      primary_feast,
      primary_color,
      date_description: current_day,
      season: { colors },
    },
  } = liturgyData

  return (
    <div>
      <div className="main-card hideBody">
        <div
          className={`el-card is-always-shadow box-card ${
            primary_color || colors.join(' ')
          }`}
        >
          <div className="el-card__header">
            <div className="card-header">
              <h1>Composite Liturgy</h1>
              <h3>
                {current_day.weekday}, {current_day.month_name}{' '}
                {current_day.day}, {current_day.year}
              </h3>
              {!!primary_feast && (
                <div className="card-info">
                  <h4 className="primary-feast-heading">{primary_feast}</h4>
                </div>
              )}
            </div>
          </div>
          <div className="el-card__body" />
        </div>
      </div>

      <div className="main-body">
        <div className="small-container">
          <FontSizeSelector />
        </div>
        <div id="main" className="responsive-text line-overrides">
          <div>
            {liturgyData.modules.map((mod) => {
              return (
                <>
                  {mod.lines.map((line) => {
                    switch (line.line_type) {
                      case 'heading':
                        return (
                          <h2 className="responsive-text">{line.content}</h2>
                        )
                      case 'subheading':
                        return (
                          <h4 className="mt-2 mb-2 pt-2 pb-2">
                            {line.content}
                          </h4>
                        )
                      case 'citation':
                        return <h5>{line.content}</h5>
                      case 'html':
                        return (
                          <div
                            className="officeHTML"
                            dangerouslySetInnerHTML={{ __html: line.content }}
                          />
                        )
                      case 'leader':
                      case 'reader': {
                        const className = [
                          String(line.indented) ?? '',
                          line.extra_space_before ? 'extra-space-before' : '',
                        ].join(' ')
                        return (
                          <p className={className}>
                            {!!line.preface && (
                              <>
                                <sup>{line.preface}&nbsp;</sup>&nbsp;
                              </>
                            )}
                            {line.content}
                          </p>
                        )
                      }
                      case 'congregation':
                        return (
                          <p className={String(line.indented) ?? ''}>
                            {!!line.preface && (
                              <>
                                <sup>{line.preface}&nbsp;</sup>&nbsp;
                              </>
                            )}
                            <strong>{line.content}</strong>
                          </p>
                        )
                      case 'congregation_dialogue':
                        return (
                          <div className="row">
                            <div className="column left">
                              <p className="rubric">
                                <em>
                                  <small>People</small>
                                </em>
                              </p>
                            </div>
                            <div className="column right">
                              <p>
                                <strong>{line.content}</strong>
                              </p>
                            </div>
                          </div>
                        )
                      case 'leader_dialogue':
                        return (
                          <div className="row">
                            <div className="column left">
                              <p className="rubric">
                                <em>
                                  <small>Officiant</small>
                                </em>
                              </p>
                            </div>
                            <div className="column right">
                              <p>{line.content}</p>
                            </div>
                          </div>
                        )
                      case 'rubric':
                        return (
                          <p className="rubric">
                            <em>{line.content}</em>
                          </p>
                        )
                      case 'spacer':
                        return <div className="spacer" />
                      default:
                        return null
                    }
                  })}
                </>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
